import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, isSignInWithEmailLink, sendSignInLinkToEmail, signInWithEmailLink } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingAnimation from './LoadingAnimation';

// Importing from firebase ensures initializeApp runs before getAuth() below
// — same pattern as League Morning Star
import { db } from '../firebase'; // eslint-disable-line no-unused-vars

const auth = getAuth();

const Login = () => {
  const [email, setEmail] = useState('');
  const [view, setView] = useState('login');
  const [dataReady, setDataReady] = useState(false);
  const [error, setError] = useState('');
  const [needsEmail, setNeedsEmail] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/admin';

  const actionCodeSettings = {
    url: `${window.location.origin}/login?email=${encodeURIComponent(email)}`,
    handleCodeInApp: true
  };

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        navigate(redirectTo);
      } else {
        if (isSignInWithEmailLink(auth, window.location.href)) {
          const urlParams = new URLSearchParams(window.location.search);
          const emailFromUrl = urlParams.get('email');
          const savedEmail = localStorage.getItem('stt_login_email') || emailFromUrl;

          if (savedEmail) {
            signInWithEmailLink(auth, savedEmail, window.location.href)
              .then(() => {
                localStorage.removeItem('stt_login_email');
                navigate(redirectTo);
              })
              .catch((err) => {
                console.log(err.code, err.message);
                setError('Sign-in link was invalid or expired. Please request a new one.');
                setDataReady(true);
              });
          } else {
            setNeedsEmail(true);
            setDataReady(true);
          }
        } else {
          setDataReady(true);
        }
      }
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function sendLink() {
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setDataReady(false);
    setError('');
    sendSignInLinkToEmail(auth, email.trim(), {
      url: `${window.location.origin}/login?email=${encodeURIComponent(email.trim())}`,
      handleCodeInApp: true
    })
      .then(() => {
        localStorage.setItem('stt_login_email', email.trim());
        setView('check-email');
        setDataReady(true);
      })
      .catch((err) => {
        console.log(err.code, err.message);
        setError(`Error: ${err.code} — ${err.message}`);
        setDataReady(true);
      });
  }

  function confirmAndSignIn() {
    if (!confirmEmail.trim()) return;
    signInWithEmailLink(auth, confirmEmail.trim(), window.location.href)
      .then(() => {
        localStorage.removeItem('stt_login_email');
        navigate(redirectTo);
      })
      .catch((err) => {
        console.log(err.code, err.message);
        setError('Sign-in link was invalid or expired. Please request a new one.');
        setNeedsEmail(false);
        setDataReady(true);
      });
  }

  if (dataReady === false) {
    return (
      <div className="stt-login">
        <LoadingAnimation />
      </div>
    );
  }

  if (needsEmail) {
    return (
      <div className="stt-login">
        <div className="stt-login-box">
          <h1 className="stt-login-title">Confirm your email</h1>
          <p className="stt-login-subtitle">
            Please enter the email address you used to request the sign-in link.
          </p>
          <div style={{ marginBottom: '16px' }}>
            <label className="stt-form-label" htmlFor="confirm-email">Email Address</label>
            <input
              id="confirm-email"
              type="email"
              className="form-control"
              value={confirmEmail}
              onChange={e => setConfirmEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmAndSignIn()}
              placeholder="you@example.com"
              autoFocus
            />
          </div>
          {error && <p style={{ color: 'red', fontSize: '0.85rem', marginBottom: '12px' }}>{error}</p>}
          <button className="btn stt-btn-primary w-100" onClick={confirmAndSignIn}>
            Confirm &amp; Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stt-login">
      <div className="stt-login-box">
        {view === 'login' ? (
          <>
            <h1 className="stt-login-title">Login</h1>
            <p className="stt-login-subtitle">
              Enter your email and we'll send you a sign-in link.<br />
              <span style={{ fontSize: '0.8rem', color: '#999' }}>First time? Just enter your email — no separate sign-up needed.</span>
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label className="stt-form-label" htmlFor="login-email">Email Address</label>
              <input
                id="login-email"
                type="email"
                className="form-control"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendLink()}
                placeholder="you@example.com"
              />
            </div>
            {error && (
              <p style={{ color: 'red', fontSize: '0.85rem', marginBottom: '12px' }}>{error}</p>
            )}
            <button className="btn stt-btn-primary w-100" onClick={sendLink}>
              Send Sign-In Link
            </button>
          </>
        ) : (
          <>
            <h1 className="stt-login-title">Check your email</h1>
            <p className="stt-login-subtitle" style={{ marginBottom: 0 }}>
              We sent a sign-in link to <strong>{email}</strong>. Tap the link in the email to log in.
            </p>
            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '0.82rem', color: '#888' }}>
              Open the link in <strong>Safari</strong> (or your main browser) for best results.
            </p>
            <p style={{ textAlign: 'center', marginTop: '8px' }}>
              <button
                style={{ background: 'none', border: 'none', color: '#555', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => { setView('login'); setEmail(''); }}
              >
                Use a different email
              </button>
            </p>
            <p style={{ textAlign: 'center', marginTop: '12px', fontSize: '0.78rem', color: '#999' }}>
              Don't see it? Check your junk or spam folder.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
